/**
 * F 135 — the saved-view contract, in the BRAIN.
 *
 * F 134 found the pieces existed but in the wrong place: `viewConfig.ts` lived
 * under `src/ui/` and wrote straight to `localStorage`, which made a saved
 * view a browser detail rather than part of the platform — not portable, not
 * queryable, not synchronised with the database it describes.
 *
 * This module moves the contract into `src/core/`, persists through a
 * `StorageAdapter` like every other entity, and adds the three things a
 * Notion-style view actually needs and the old shape lacked: `filter`,
 * `visiblePropertyIds` and `propertyOrder`.
 *
 * NOT duplicated on purpose: visibility and ordering are also expressible on
 * `PropertyDefinition` (`visible`, `position`). A view's
 * `visiblePropertyIds` / `propertyOrder` are an OVERRIDE, not a second source
 * of truth — see {@link resolveVisibleProperties} and
 * {@link resolvePropertyOrder}, which fall back to the definitions when the
 * view says nothing.
 *
 * The adapter is always passed in. No module-level singleton, so the same
 * function round-trips through FilesystemStorageAdapter, PocketBaseAdapter, or
 * anything else implementing `StorageAdapter`.
 */

import type { StorageAdapter } from '../storage-adapter';
import { propertyFilterToFilterRules } from './propertyFilterBridge';
import type { PropertyDefinition, PropertyValue } from '../../types/models/Property';
// F 509 — THE GRANULARITY LIST, FOR THE PERSISTENCE VALIDATION.
import { GRANULARITIES, type GroupGranularity } from './groupRecords';

/**
 * The layouts FCr implements.
 *
 * F 135 shipped six. F 136 added four generic ones so a consumer can reach
 * for the common shapes without writing a renderer:
 *
 *   gallery  — image-forward grid            (F 135)
 *   kanban   — columns grouped by a property (F 135)
 *   list     — rows, one per item           (F 135)
 *   calendar — time-based grid              (F 135)
 *   timeline — dated rail                   (F 135)
 *   recent   — reverse-chronological feed   (F 135)
 *
 *   card     — VERTICAL STACK of self-contained items. Unlike `list`, each
 *              item is a card with its own chrome (stripe, indicator,
 *              checkbox). F 136.
 *   table    — GRID: rows are items, columns are properties, headers
 *              sortable. F 136.
 *   dense    — ONE-LINE rows, optionally grouped under a category header,
 *              no card chrome. F 136. (Homeschool's "compact".)
 *   detail   — STRUCTURED single-item page: sections of prose with optional
 *              per-section state, plus embedded media. Composable, so a
 *              consumer can preset it for their own shape — Homeschool's
 *              "lesson" is `detail` with a preset. F 136.
 *
 * NOTE: "lesson" is deliberately NOT here. It is a consumer's vocabulary,
 * not the platform's — the same split FCr uses for icon sets and pickers.
 */
export type ViewLayout =
  // F 135
  | 'gallery'
  | 'kanban'
  | 'list'
  | 'calendar'
  | 'timeline'
  | 'recent'
  // F 136
  | 'card'
  | 'table'
  | 'dense'
  | 'detail';

export const VIEW_LAYOUTS: readonly ViewLayout[] = [
  'gallery', 'kanban', 'list', 'calendar', 'timeline', 'recent',
  'card', 'table', 'dense', 'detail',
];

/** Layouts F 136 added, for consumers that want to reason about the delta. */
export const F136_LAYOUTS: readonly ViewLayout[] = ['card', 'table', 'dense', 'detail'];

/** One-line description per layout, so a consumer can render an "add view" menu. */
export const VIEW_LAYOUT_DESCRIPTIONS: Readonly<Record<ViewLayout, string>> = {
  gallery:  'Image-forward grid of items.',
  kanban:   'Columns grouped by a property.',
  list:     'One row per item.',
  calendar: 'Time-based grid.',
  timeline: 'Dated rail.',
  recent:   'Reverse-chronological feed.',
  card:     'Vertical stack of self-contained item cards.',
  table:    'Grid — rows are items, columns are properties, sortable.',
  dense:    'One-line rows, optionally grouped, no card chrome.',
  detail:   'Structured single-item page with sections and media.',
};

export type SortDir = 'asc' | 'desc';

export type GroupByMode =
  | 'status' | 'priority' | 'assignee' | 'tag' | 'due-week' | 'none'
  /**
   * F 504 — THE DATE MODES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-10-31: before this, `grep -rn "granularity|
   * byDay|byMonth|Uncategorized" src/` returned ZERO. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ EVERY
   * existing mode is PROPERTY-SHAPED — status, priority, assignee, tag,
   * due-week — so "group by Created Date" HAD NO MODE. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒
   * ⇒⇒⇒ THAT IS THE REAL GAP, and it is filled by
   * `src/core/views/groupRecords.ts`.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A GRANULARITY MAY NOT RESTATE A MODE.**
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The dispatch also named 'status' and 'exact' as
   * granularities. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ 'status' is ALREADY a mode
   * (`groupBy: 'status'`), so a second spelling of it beside the first
   * would be exactly the 3-SOURCE DRIFT this file's own history records
   * removing. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ 'exact' is a granularity of
   * a DATE, not a mode of its own.
   */
  | 'created-day' | 'created-month'
  /**
   * F 509 — THE GENERAL DATE MODE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-11-01: EVERY MODE IN THIS UNION IS HARDCODED,
   * AND `GROUP_BY_LABELS` IN `ViewSwitcher.tsx` IS `Record<GroupByMode,
   * string>` — EXHAUSTIVE BY TYPE. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ **⇒⇒⇒ A NEW MODE PER
   * USER PROPERTY WOULD BE UNBOUNDED AND WOULD BREAK THAT EXHAUSTIVENESS.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ SO THERE IS EXACTLY ONE NEW MODE, `'date'`,
   * AND THE PROPERTY IS NAMED BY `groupByField` — THE SPLIT THE DATA MODEL
   * ALREADY HAD AND NEVER WIRED.** ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ `groupBy` SAYS THE
   * FAMILY; `groupByField` SAYS WHICH PROPERTY; `groupGranularity` SAYS HOW
   * COARSELY.
   */
  | 'date';

export type FilterOperator =
  | 'is' | 'is-not'
  | 'contains' | 'not-contains'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'is-empty' | 'is-not-empty'
  | 'is-checked' | 'is-unchecked'
  | 'is-before' | 'is-after' | 'is-within'
  // F 283 - a GROUPED OR. `value` is an array and the row passes when the
  // field matches ANY of them. This is the operator that lets a
  // `PropertyFilter` (`{status: ['a','b']}`) cross into `FilterRule[]`
  // without being flattened into an AND that matches nothing.
  | 'is-any-of';

export interface FilterRule {
  propertyId: string;
  operator: FilterOperator;
  /** Unused by the unary operators (`is-empty`, `is-checked`, …). */
  value?: unknown;
}

export interface SortRule {
  /** A property id, or one of the row-level pseudo-properties. */
  propertyId: string;
  dir: SortDir;
}

/** Row-level fields a view can sort or filter on without a property id. */
export const ROW_PSEUDO_PROPERTIES = ['title', 'created', 'updated'] as const;
export type RowPseudoProperty = (typeof ROW_PSEUDO_PROPERTIES)[number];

/** `card` — a vertical stack of self-contained item cards (F 136). */
export interface CardLayoutOptions {
  /** Property whose value tints a left colour stripe. */
  stripePropId?: string;
  /** Property used as a ring/indicator badge. */
  indicatorPropId?: string;
  /** Property rendered as a checkbox. */
  checkboxPropId?: string;
  /** Property rendered under the title as a subtitle. */
  subtitlePropId?: string;
}

/** `table` — rows are items, columns are properties (F 136). */
export interface TableLayoutOptions {
  /** Cells editable in place. Read-only when false. Default false. */
  editable?: boolean;
  /** Rendered as the row's leading cell, before the property columns. */
  titlePropId?: string;
}

/** `dense` — one-line rows, grouped (F 136). */
export interface DenseLayoutOptions {
  /** Property whose value becomes the group heading. */
  groupPropId?: string;
  /** Property rendered right-aligned as the row's value. */
  valuePropId?: string;
  /** Render a checkbox per row. */
  checkbox?: boolean;
}

/** One section of a `detail` page (F 136). */
export interface DetailSection {
  title: string;
  /** Static prose. */
  body?: string;
  /** Or: a property whose value becomes the section body. */
  propertyId?: string;
  /** Embedded image URL. */
  imageUrl?: string;
  /** Consumer-defined state, e.g. { checked: true }. FCr never interprets it. */
  state?: Record<string, unknown>;
}

/** `detail` — a structured single-item page (F 136). */
export interface DetailLayoutOptions {
  sections?: DetailSection[];
  /** Media shown at the top, before the sections. */
  coverImageUrl?: string;
}

export type ViewLayoutOptions =
  | CardLayoutOptions | TableLayoutOptions | DenseLayoutOptions | DetailLayoutOptions;

export interface ViewConfig {
  id: string;
  /**
   * Carried from the pre-F 135 `DatabaseViewConfig`. The F 135 field list
   * omits it, but dropping it would lose data during migration — every view
   * already stored in localStorage has it, and `applyViewConfig` needs it to
   * know which items to load.
   */
  databaseId: string;
  name: string;
  layout: ViewLayout;
  filter: FilterRule[];
  sort: SortRule[];
  /**
   * Per-view override of `PropertyDefinition.visible`. When empty, the
   * definitions decide — F 134 established those fields already exist.
   */
  visiblePropertyIds: string[];
  /** Per-view override of `PropertyDefinition.position`. When empty, the definitions decide. */
  propertyOrder: string[];
  groupBy?: GroupByMode;
  /**
   * F 504 — WHICH FIELD TO GROUP BY.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ `groupBy` says HOW to group; this says WHAT BY.
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The dispatch's example is "Created Date", which is
   * a FIELD, not a mode — so a mode alone cannot express it. ⇒⇒⇒⇒⇒⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THIS LIVES ON THE VIEW, NOT ON THE
   * DATABASE RECORD.** ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ MEASURED: `groupBy` has always
   * lived on the view config (core line 202, UI line 101), and the
   * dispatch's `groupBy?: string` on the `Database` record would have been a
   * SECOND home for one concept.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ OPTIONAL, and additive: a view that says nothing
   * keeps grouping exactly as it did.
   */
  groupByField?: string;
  /**
   * F 509 — HOW COARSELY TO BUCKET THE DATE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-11-01: F 504's COMMENT IS EXPLICIT — "A
   * GRANULARITY MAY NOT RESTATE A MODE." ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ `created-day`
   * RESTATES `groupBy`, AND THE F 506 SHIP LEFT IT THAT WAY. ⇒⇒⇒⇒⇒⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE FIX IS A REAL, INDEPENDENT AXIS.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE VALUES ARE NOTION'S** — RESEARCHED
   * 2026-11-01 FROM NOTION'S OWN API DOCS: `"group_by": "relative" | "day" |
   * ⇒⇒□ ⇒⇒□ "week" | "month" | "year"`.
   */
  groupGranularity?: import('./groupRecords').GroupGranularity;
  /**
   * F 136 — layout-specific options. Additive: `applyViewConfig` ignores this
   * entirely, so adding a layout never changes how filtering, sorting or
   * projection behave. Renderers read it; the brain does not.
   */
  layoutOptions?: ViewLayoutOptions;
  /**
   * F 506 — THE BOARD'S PRESENTATION TOGGLES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THESE LIVE ON THE VIEW, BESIDE
   * `groupBy`, BECAUSE THEY ARE A PROPERTY OF THE VIEW AND NOT OF THE
   * APP.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ ONE BOARD WRAPS ITS CARDS AND
   * ANOTHER DOES NOT; A GLOBAL TOGGLE WOULD MAKE THAT IMPOSSIBLE TO
   * EXPRESS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THEY ARE PERSISTED WITH EVERYTHING
   * ELSE, BECAUSE THE PUSH/PULL TEST IS ABOUT A RELOAD. ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ A TOGGLE THAT RESETS ON RELOAD IS NOT A SETTING;
   * IT IS A MOOD.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ DEFAULTS ARE STATED HERE, NOT AT THE READ
   * SITE, BECAUSE THE READ SITE IS THE UI AND THE WRITE SITE IS THE
   * GRAPH AND THE TWO MUST AGREE. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
   * `wrapContent` DEFAULTS FALSE (clamped), `colorColumns` DEFAULTS TRUE
   * (a board without colour is a list of grey boxes), `showIcons`
   * DEFAULTS TRUE.**
   */
  wrapContent?: boolean;
  colorColumns?: boolean;
  showIcons?: boolean;
  /** Layout hints, carried over from the pre-F 135 shape. */
  coverPropId?: string;
  datePropId?: string;
  titlePropId?: string;
  createdAt: number;
  updatedAt: number;
}

const VIEW_KEY_PREFIX = 'view:';
const LEGACY_LOCALSTORAGE_PREFIX = 'freshcards:views:';

export const viewKey = (databaseId: string): string => `${VIEW_KEY_PREFIX}${databaseId}`;

// ─── Defaults ──────────────────────────────────────────────────────────────

/**
 * F 135 — the six layouts FCr actually implements.
 *
 * NOTE: the F 135 dispatch asked for seeds named "Compact / Card / Lesson /
 * Table / Gallery / Calendar". Three of those (`card`, `lesson`, `table`) and
 * `compact` — which is a prop on `DayColumnBody`, not a view type — DO NOT
 * EXIST in FCr. Seeding them would have meant inventing four views the
 * dispatch's own scope boundary forbids ("Do NOT build consumer-specific
 * views"). Seeded what is real instead.
 */
export const DEFAULT_VIEW_CONFIGS: ReadonlyArray<
  Omit<ViewConfig, 'createdAt' | 'updatedAt' | 'databaseId'>
> = [
  { id: 'view_gallery', name: 'Gallery', layout: 'gallery',   filter: [], sort: [{ propertyId: 'updated', dir: 'desc' }], visiblePropertyIds: [], propertyOrder: [], coverPropId: undefined },
  { id: 'view_list',    name: 'List',    layout: 'list',      filter: [], sort: [{ propertyId: 'title',   dir: 'asc'  }], visiblePropertyIds: [], propertyOrder: [], titlePropId: undefined },
  { id: 'view_kanban',  name: 'Kanban',  layout: 'kanban',    filter: [], sort: [], visiblePropertyIds: [], propertyOrder: [], groupBy: 'status' },
  { id: 'view_calendar',name: 'Calendar',layout: 'calendar',  filter: [], sort: [], visiblePropertyIds: [], propertyOrder: [], datePropId: undefined },
  { id: 'view_timeline',name: 'Timeline',layout: 'timeline',  filter: [], sort: [{ propertyId: 'date', dir: 'asc' }], visiblePropertyIds: [], propertyOrder: [], datePropId: undefined },
  { id: 'view_recent',  name: 'Recent',  layout: 'recent',    filter: [], sort: [{ propertyId: 'updated', dir: 'desc' }], visiblePropertyIds: [], propertyOrder: [] },
  // F 136 — defaults for the four generic layouts. They are SEEDED, not
  // forced: a consumer can delete any and create their own. `layoutOptions`
  // is empty here on purpose — the stripe/indicator/column wiring is
  // property-id specific and belongs to whoever has the schema.
  { id: 'view_card',    name: 'Cards',   layout: 'card',      filter: [], sort: [{ propertyId: 'updated', dir: 'desc' }], visiblePropertyIds: [], propertyOrder: [], layoutOptions: { checkbox: undefined } as never },
  { id: 'view_table',   name: 'Table',   layout: 'table',     filter: [], sort: [], visiblePropertyIds: [], propertyOrder: [], layoutOptions: { editable: false } },
  { id: 'view_dense',   name: 'Dense',   layout: 'dense',     filter: [], sort: [{ propertyId: 'title', dir: 'asc' }], visiblePropertyIds: [], propertyOrder: [], layoutOptions: {} },
  { id: 'view_detail',  name: 'Detail',  layout: 'detail',    filter: [], sort: [], visiblePropertyIds: [], propertyOrder: [], layoutOptions: { sections: [] } },
];

export function createDefaultViews(databaseId: string): ViewConfig[] {
  const now = Date.now();
  return DEFAULT_VIEW_CONFIGS.map((v) => ({ ...v, databaseId, createdAt: now, updatedAt: now }));
}

// ─── Coercion — read-time, never throws ─────────────────────────────────────

function asArray<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }
function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

const LAYOUT_SET = new Set<string>(VIEW_LAYOUTS);

/** Accepts both the F 135 shape and the pre-F 135 `DatabaseViewConfig` shape. */
/**
 * F 509 — THE VIEW PATCH, AS A PURE FUNCTION OF THE PREVIOUS STATE.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ AND IT IS PURE *BECAUSE* OF A MEASURED BUG. ⇒⇒⇒⇒⇒⇒⇒⇒
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01 AGAINST THE LIVE GRAPH: A
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ KANBAN VIEW WAS STORED AS `{ groupBy: "date" }` AND
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOTHING ELSE — `groupByField` AND
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `groupGranularity` WERE ABSENT.** ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE CAUSE: `ViewSwitcher`'s DATE-PROPERTY ROW
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ CALLS `onChangeDateGrouping` AND THEN
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `onChangeGroupBy` — TWO CALLS IN ONE TICK — AND THE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OLD IMPLEMENTATION COMPUTED BOTH FROM A *CAPTURED*
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `db`, WHICH IS THE PRE-UPDATE VALUE BECAUSE REACT'S
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `setState` IS ASYNCHRONOUS. THE SECOND CALL DROPPED
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE FIRST CALL'S FIELDS.**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE MERGE MUST BE A FUNCTION OF THE PREVIOUS
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STATE — WHICH IS EXACTLY WHAT A FUNCTIONAL STATE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ UPDATER CALLS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND BY
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PUTTING IT HERE — IN CORE, PURE, TESTABLE — THE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PRODUCTION CODE AND THE TEST CALL THE *SAME*
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FUNCTION, SO A TEST CANNOT PASS AGAINST A COPY
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THAT THE PRODUCT DOES NOT USE.**
 *
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE VIEW IS MATCHED BY `id` FIRST AND BY ITS
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `type` SECOND** — the F 506 fix, because the graph's
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ids and the UI's ids are different strings for the
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ same view.
 */
export function patchViewInPlace<T extends { views?: unknown[] }>(
  prev: T,
  viewId: string,
  patch: Record<string, unknown>,
  viewTypeFor: (id: string, views: unknown[]) => string | undefined,
): T {
  const current = (prev.views ?? []) as Array<Record<string, unknown>>;
  const target =
    current.find((v) => v.id === viewId) ??
    current.find((v) => (v.type ?? v.layout) === viewTypeFor(viewId, current));
  if (!target) return prev;
  const nextViews = current.map((v) =>
    v === target ? { ...v, ...patch, updatedAt: Date.now() } : v,
  );
  return { ...prev, views: nextViews };
}

export function coerceViewConfig(input: unknown, databaseId = ''): ViewConfig | null {
  if (input == null || typeof input !== 'object') return null;
  const v = input as Record<string, unknown>;
  const now = Date.now();

  // `layout` is the F 135 name; `type` was the pre-F 135 one.
  const rawLayout = asString(v.layout) || asString(v.type);
  const layout = (LAYOUT_SET.has(rawLayout) ? rawLayout : 'list') as ViewLayout;

  // pre-F 135 carried sortBy/sortDir as scalars rather than a sort[] array
  const sort: SortRule[] = asArray<SortRule>(v.sort).length > 0
    ? asArray<SortRule>(v.sort)
    : (v.sortBy ? [{ propertyId: asString(v.sortBy), dir: asString(v.sortDir, 'asc') === 'desc' ? 'desc' : 'asc' }] : []);

  return {
    id: asString(v.id) || `view_${Math.random().toString(36).slice(2, 10)}`,
    databaseId: asString(v.databaseId) || databaseId,
    name: asString(v.name) || 'View',
    layout,
    // F 283 - `filter` arrives in TWO shapes, and reading only one of them
    // silently dropped filters.
    //
    // A view authored against core carries `FilterRule[]`. A view authored
    // against FCC carries `PropertyFilter` (`{status: ['a','b']}`) - and
    // `asArray<FilterRule>` returns [] for a Record, so every FCC filter
    // vanished on migration with no error. That is the "existing views
    // survive" requirement failing in the one place it is tested.
    //
    // Both are accepted; the bridge is the only thing that knows the
    // difference. `Array.isArray` is the discriminator - a Record and an
    // array are not ambiguous here.
    filter: Array.isArray(v.filter)
      ? asArray<FilterRule>(v.filter)
      : propertyFilterToFilterRules(v.filter as Record<string, string[]>),
    sort,
    visiblePropertyIds: asArray<string>(v.visiblePropertyIds),
    propertyOrder: asArray<string>(v.propertyOrder),
    ...(v.groupBy ? { groupBy: v.groupBy as GroupByMode } : {}),
    // ⇒⇒⇒⇒⇒⇒⇒⇒ F 506 — THE READER MUST KEEP THE FIELDS THE WRITER
    // SAVED. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `coerceViewConfig` IS THE
    // FUNCTION EVERY GRAPH ROUND-TRIP PASSES THROUGH** — the adapter
    // writes the view, the reader loads it and coerces it, and whatever
    // the coercer does not name is GONE. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO
    // A TOGGLE THAT IS NOT NAMED HERE SAVES PERFECTLY AND COMES BACK
    // UNSET.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND `groupByField` HAD THE SAME HOLE — IT
    // WAS WRITTEN BY F 504 AND NEVER READ BACK.
    ...(v.groupByField ? { groupByField: asString(v.groupByField) } : {}),
    // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE GRANULARITY MUST SURVIVE THE ROUND-TRIP, FOR
    // THE EXACT REASON THE F 506 TOGGLES DID NOT. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
    // `coerceViewConfig` IS THE FUNCTION EVERY GRAPH ROUND-TRIP PASSES
    // THROUGH, AND WHATEVER IT DOES NOT NAME IS GONE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ **⇒⇒□ SO A GRANULARITY THAT IS NOT NAMED HERE SAVES PERFECTLY
    // AND COMES BACK UNSET — AND A BOARD THAT WAS GROUPED BY WEEK WOULD
    // COME BACK GROUPED BY DAY.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    //
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND `groupByField` IS NAMED, BUT THE FIELD VALUE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ MUST BE ONE OF THE KNOWN GRANULARITIES — A GRAPH
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ROUND-TRIP CAN CARRY ANYTHING, AND AN UNKNOWN
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ VALUE MUST NOT REACH `dateBucketKey`.
    ...(GRANULARITIES.includes(v.groupGranularity as GroupGranularity)
      ? { groupGranularity: v.groupGranularity as GroupGranularity }
      : {}),
    ...(typeof v.wrapContent === 'boolean' ? { wrapContent: v.wrapContent } : {}),
    ...(typeof v.colorColumns === 'boolean' ? { colorColumns: v.colorColumns } : {}),
    ...(typeof v.showIcons === 'boolean' ? { showIcons: v.showIcons } : {}),
    ...(v.layoutOptions ? { layoutOptions: v.layoutOptions as ViewLayoutOptions } : {}),
    ...(v.coverPropId ? { coverPropId: asString(v.coverPropId) } : {}),
    ...(v.datePropId ? { datePropId: asString(v.datePropId) } : {}),
    ...(v.titlePropId ? { titlePropId: asString(v.titlePropId) } : {}),
    createdAt: typeof v.createdAt === 'number' ? v.createdAt : now,
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : now,
  };
}

// ─── Persistence — through the StorageAdapter, like every other entity ─────

/**
 * Load a database's views.
 *
 * On first call it also migrates any views left in `localStorage` by the
 * pre-F 135 UI-layer code, writes them through the adapter, and clears the
 * legacy key so the migration cannot run twice.
 */
export async function loadViews(
  adapter: StorageAdapter,
  databaseId: string,
): Promise<ViewConfig[]> {
  const key = viewKey(databaseId);

  const stored = await adapter.load(key).catch(() => null);
  if (Array.isArray(stored) && stored.length > 0) {
    return stored.map((v) => coerceViewConfig(v, databaseId)).filter((v): v is ViewConfig => v !== null);
  }

  const migrated = await migrateLegacyLocalStorageViews(adapter, databaseId);
  if (migrated.length > 0) return migrated;

  const seeded = createDefaultViews(databaseId);
  await saveViews(adapter, databaseId, seeded);
  return seeded;
}

/** Write the full set. Never throws — a failed save must not lose the caller's UI. */
export async function saveViews(
  adapter: StorageAdapter,
  databaseId: string,
  views: ViewConfig[],
): Promise<void> {
  try {
    await adapter.save(viewKey(databaseId), views);
  } catch (e) {
    console.error('[views] failed to persist views for', databaseId, e);
  }
}

/**
 * One-shot migration from the pre-F 135 localStorage views.
 *
 * Written through the adapter and the legacy key removed, so a later load
 * never sees the old data twice. Returns [] when there is nothing to migrate.
 */
export async function migrateLegacyLocalStorageViews(
  adapter: StorageAdapter,
  databaseId: string,
): Promise<ViewConfig[]> {
  if (typeof localStorage === 'undefined') return [];
  const legacyKey = `${LEGACY_LOCALSTORAGE_PREFIX}${databaseId}`;
  let raw: string | null = null;
  try { raw = localStorage.getItem(legacyKey); } catch { return []; }
  if (!raw) return [];

  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { parsed = null; }
  if (!Array.isArray(parsed)) {
    try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
    return [];
  }
  const views = parsed
    .map((v) => coerceViewConfig(v, databaseId))
    .filter((v): v is ViewConfig => v !== null);
  // F 280 - THE LEGACY KEY IS DELETED ONLY AFTER A VERIFIED WRITE.
  //
  // This used to be:
  //
  //     if (views.length > 0) await saveViews(adapter, databaseId, views);
  //     try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
  //
  // which is data loss wearing a migration's clothes. `saveViews` catches its
  // own error and returns void:
  //
  //     try { await adapter.save(viewKey(databaseId), views); }
  //     catch (e) { console.error('[views] failed to persist views for', databaseId, e); }
  //
  // so a failed adapter write was indistinguishable from a successful one, and
  // the legacy key was removed either way. A quota error, a private-mode
  // browser or a full IndexedDB would silently delete every saved view the user
  // had, with the only trace a console line nobody reads.
  //
  // The fix is a read-back, because `save` returns void and there is no
  // boolean to check. The legacy copy is the user's only copy, so it is not
  // destroyed until the new one is known to exist.
  if (views.length > 0) {
    await saveViews(adapter, databaseId, views);
    const written = await adapter.load(viewKey(databaseId)).catch(() => null);
    if (Array.isArray(written) && written.length > 0) {
      try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
    } else {
      // Keep the legacy key. The next load will try again, and the user's views
      // survive meanwhile. This is the whole point of migrating last.
      console.error(
        '[views] legacy migration NOT verified — leaving', legacyKey,
        'in place. Saved views were not deleted.',
      );
    }
  } else {
    // Nothing usable in the legacy copy. It is not a view set, so removing it
    // loses no views — but say so rather than deleting in silence.
    try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
  }
  return views;
}

/**
 * F 378 — COPY-ONLY view migration.
 *
 * WHY THIS IS A SEPARATE FUNCTION AND NOT A FLAG ON THE EXISTING ONE
 * --------------------------------------------------------------
 * `migrateLegacyLocalStorageViews` DELETES `freshcards:views:<id>` once the
 * core write is verified. That is the correct end state for a completed
 * migration, and it is the behaviour F 280 wanted.
 *
 * F 378's BOUNDS says "do not delete the legacy data (just copy it)". Adding a
 * `keepLegacy` flag to the existing function would mean every future caller
 * chooses its own data-destruction semantics through a boolean, and the default
 * (`false` — delete) is the dangerous one. This is a distinct, named operation
 * with the destructive branch simply absent.
 *
 * IT IS IDEMPOTENT BY CONSTRUCTION
 * --------------------------------
 * Run twice, the second run finds `view:<dbId>` already populated and returns
 * `skipped: true` without writing. It never re-coerces over a good core copy,
 * so a user's edits made in the core layer after the first run are not clobbered
 * by a second invocation reading the stale legacy blob.
 *
 * The legacy key is left byte-identical. That is the rollback: if the core
 * layer turns out to be wrong, the original data is still exactly where it was.
 *
 * VERIFIED BEFORE DECLARING SUCCESS
 * ---------------------------------
 * `saveViews` swallows its own errors and returns void, so a failed adapter
 * write is indistinguishable from a successful one. This reports `verified:
 * false` in that case rather than claiming a migration that did not happen.
 */
export interface CopyMigrationResult {
  /** Database id that was inspected. */
  databaseId: string;
  /** Views written to `view:<dbId>` — 0 when nothing was copied. */
  copied: number;
  /** Where the data was read from. Always the legacy key. */
  from: string;
  /** Where it was written. Always the core key. */
  to: string;
  /** False when the read-back found nothing — the write did NOT land. */
  verified: boolean;
  /** True when the core key already held views, so nothing was overwritten. */
  skipped: boolean;
  /** Why it returned early. Null on a successful copy. */
  reason: 'no_legacy' | 'already_migrated' | 'unparseable' | 'empty' | 'write_unverified' | null;
  /** The legacy key is always left intact by this function. Stated, not implied. */
  legacyRemoved: false;
}

export async function copyLegacyViewsToCore(
  adapter: StorageAdapter,
  databaseId: string,
): Promise<CopyMigrationResult> {
  const legacyKey = `${LEGACY_LOCALSTORAGE_PREFIX}${databaseId}`;
  const targetKey = viewKey(databaseId);
  const base: CopyMigrationResult = {
    databaseId,
    copied: 0,
    from: legacyKey,
    to: targetKey,
    verified: false,
    skipped: false,
    reason: null,
    legacyRemoved: false,
  };

  if (typeof localStorage === 'undefined') return { ...base, reason: 'no_legacy' };

  // Idempotence: never overwrite a populated core copy. This is what makes a
  // second run safe after a user has edited views through the core layer.
  const existingCore = await adapter.load(targetKey).catch(() => null);
  if (Array.isArray(existingCore) && existingCore.length > 0) {
    return { ...base, copied: existingCore.length, verified: true, skipped: true, reason: 'already_migrated' };
  }

  let raw: string | null = null;
  try { raw = localStorage.getItem(legacyKey); } catch { /* treat as absent */ }
  if (!raw) return { ...base, reason: 'no_legacy' };

  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { parsed = null; }
  if (!Array.isArray(parsed)) {
    // NOT deleted, unlike the original. An unparseable blob is left for a human.
    return { ...base, reason: 'unparseable' };
  }

  // Same coercion as the original migration — it already handles both the F 135
  // `FilterRule[]` shape and the FCC `PropertyFilter` shape (F 283).
  const views = parsed
    .map((v) => coerceViewConfig(v, databaseId))
    .filter((v): v is ViewConfig => v !== null);

  if (views.length === 0) return { ...base, reason: 'empty' };

  await saveViews(adapter, databaseId, views);

  const written = await adapter.load(targetKey).catch(() => null);
  const verified = Array.isArray(written) && written.length > 0;
  if (!verified) {
    return { ...base, copied: views.length, reason: 'write_unverified' };
  }

  return { ...base, copied: views.length, verified: true };
}

/** Does a legacy localStorage set exist for this database? Used by tests. */
export function hasLegacyLocalStorageViews(databaseId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(`${LEGACY_LOCALSTORAGE_PREFIX}${databaseId}`) !== null; }
  catch { return false; }
}

// ─── Visibility / order resolution — reference, do not duplicate ───────────

/**
 * Which properties this view shows, in order.
 *
 * The view's own `visiblePropertyIds` / `propertyOrder` win when present;
 * otherwise `PropertyDefinition.visible` and `.position` decide. That is the
 * whole point of referencing them rather than copying them.
 */
export function resolveVisibleProperties(
  view: Pick<ViewConfig, 'visiblePropertyIds' | 'propertyOrder'>,
  defs: PropertyDefinition[],
): PropertyDefinition[] {
  const byId = new Map(defs.map((d) => [d.id, d]));

  if (view.propertyOrder.length > 0) {
    const ordered = view.propertyOrder
      .map((id) => byId.get(id))
      .filter((d): d is PropertyDefinition => d !== undefined);
    if (view.visiblePropertyIds.length > 0) {
      return ordered.filter((d) => view.visiblePropertyIds.includes(d.id));
    }
    return ordered;
  }

  if (view.visiblePropertyIds.length > 0) {
    return view.visiblePropertyIds
      .map((id) => byId.get(id))
      .filter((d): d is PropertyDefinition => d !== undefined);
  }

  // neither set on the view — the definitions are the single source of truth
  return defs
    .filter((d) => d.visible !== false)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

// ─── The selector ──────────────────────────────────────────────────────────

/** The row shape the selector works on — deliberately minimal, not DatabaseItem. */
export interface ViewRow {
  id: string;
  title?: string;
  properties?: PropertyValue[];
  createdAt?: string | Date;
  updatedAt?: string | Date;
  [k: string]: unknown;
}

const propOf = (row: ViewRow, propertyId: string): unknown =>
  (row.properties ?? []).find((p) => p?.propertyId === propertyId)?.value;

function readField(row: ViewRow, key: string): unknown {
  if (key === 'title') return row.title ?? '';
  if (key === 'created') return row.createdAt ?? '';
  if (key === 'updated') return row.updatedAt ?? '';
  return propOf(row, key);
}

/**
 * The comparable form of a property value. Select/status/person properties are
 * stored as objects (`{ id, name, color }`), so `is 'Todo'` has to match the
 * object's label rather than fail an identity comparison. An array collapses
 * to its members, so `is 'Todo'` also matches a multi-select containing it.
 */
function comparable(value: unknown): unknown[] {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.flatMap(comparable);
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    for (const k of ['name', 'label', 'title', 'text', 'id']) {
      if (typeof o[k] === 'string') return [o[k]];
    }
    return [];
  }
  return [value];
}

function matches(rule: FilterRule, row: ViewRow): boolean {
  const raw = readField(row, rule.propertyId);
  const empty = raw === undefined || raw === null || raw === '' ||
    (Array.isArray(raw) && raw.length === 0);

  switch (rule.operator) {
    case 'is-empty': return empty;
    case 'is-not-empty': return !empty;
    case 'is-checked': return raw === true;
    case 'is-unchecked': return raw !== true;
    case 'contains': {
      const needle = String(rule.value ?? '').toLowerCase();
      return comparable(raw).some((x) => String(x).toLowerCase().includes(needle));
    }
    case 'not-contains': return !matches({ ...rule, operator: 'contains' }, row);
    case 'is': return comparable(raw).includes(rule.value);
    case 'is-not': return !comparable(raw).includes(rule.value);
    // F 283 - the grouped OR. `comparable` normalises a scalar OR an array
    // field into a list, so this works whether the row holds `'a'` or
    // `['a','b']`. An EMPTY candidate list matches nothing, because "matches
    // any of none" is not a filter anyone means, and silently returning true
    // there would drop the whole filter.
    case 'is-any-of': {
      const wanted = Array.isArray(rule.value) ? rule.value : [rule.value];
      if (wanted.length === 0) return false;
      const have = comparable(raw);
      return wanted.some((w) => have.includes(w));
    }
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const a = Number(raw), b = Number(rule.value);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      return rule.operator === 'gt' ? a > b : rule.operator === 'gte' ? a >= b
        : rule.operator === 'lt' ? a < b : a <= b;
    }
    case 'is-before': case 'is-after': case 'is-within': {
      const t = new Date(String(raw)).getTime();
      const v = new Date(String(rule.value)).getTime();
      if (Number.isNaN(t) || Number.isNaN(v)) return false;
      return rule.operator === 'is-before' ? t < v : rule.operator === 'is-after' ? t > v : t === v;
    }
    default: return true;   // unknown operator must not silently drop rows
  }
}

const isEmptyVal = (v: unknown): boolean =>
  v === undefined || v === null || v === '' ||
  (Array.isArray(v) && v.length === 0);

/** Direction-agnostic. Empties are handled by the caller, not here: the desc
 *  branch negates this result, so an "empties last" rule inside it would flip
 *  to "empties first" under desc. */
function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
  const ta = new Date(String(a)).getTime(), tb = new Date(String(b)).getTime();
  if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
  return String(a).localeCompare(String(b));
}

/** Filter + sort only. Pure, so it is testable without an adapter. */
export function applyViewRules<T extends ViewRow>(rows: T[], view: ViewConfig): T[] {
  let out = rows;
  // every rule must pass (AND) — the common Notion semantics
  for (const rule of view.filter ?? []) out = out.filter((r) => matches(rule, r));
  const rules = view.sort ?? [];
  if (rules.length > 0) {
    out = [...out].sort((x, y) => {
      for (const rule of rules) {
        const a = readField(x, rule.propertyId);
        const b = readField(y, rule.propertyId);
        // Empties last in BOTH directions. Sorting them first — the natural
        // result of `undefined < everything` — is never what "sort by due
        // date" means, and it puts undated work above dated work in desc too.
        if (isEmptyVal(a) && isEmptyVal(b)) continue;
        if (isEmptyVal(a)) return 1;
        if (isEmptyVal(b)) return -1;
        const c = compare(a, b);
        if (c !== 0) return rule.dir === 'desc' ? -c : c;
      }
      return 0;
    });
  }
  return out;
}

export interface ApplyViewOptions {
  /** The item key to read from the adapter. Defaults to `item:<databaseId>`. */
  storageKey?: string;
}

/**
 * The full selector: fetch a database's items through the adapter, then
 * filter, sort and project per the view.
 *
 * Additive — `createQuery(adapter)` is untouched and unaffected.
 * Returns rows whose non-declared properties have been **projected down** to
 * the view's visible set, in the view's order.
 */
export async function applyViewConfig<T extends ViewRow>(
  adapter: StorageAdapter,
  view: ViewConfig,
  defs: PropertyDefinition[] = [],
  opts: ApplyViewOptions = {},
): Promise<T[]> {
  const key = opts.storageKey ?? `item:${view.databaseId ?? ''}`;
  const raw = await adapter.load(key).catch(() => null);
  const rows: T[] = Array.isArray(raw) ? (raw as T[]) : [];
  const filtered = applyViewRules(rows, view);

  const visible = resolveVisibleProperties(view, defs);
  if (visible.length === 0) return filtered;

  // Emit in the view's order, not the row's. Filtering alone would honour
  // `visiblePropertyIds` but silently ignore `propertyOrder`, which is the
  // field a drag-to-reorder editor actually writes.
  const order = visible.map((d) => d.id);
  const rank = new Map(order.map((id, i) => [id, i]));
  return filtered.map((row) => ({
    ...row,
    properties: (row.properties ?? [])
      .filter((p) => rank.has(p.propertyId))
      .sort((a, b) => (rank.get(a.propertyId)! - rank.get(b.propertyId)!)),
  }));
}
